import { equalSplitShares as sharedEqualSplitShares } from '@dale/money';

import type { TabItem, TabRecord } from '../api/schemas';

/**
 * The counter's own preview of what a settlement will cost, kept in exact step
 * with `services/api/src/tab-totals.ts`. The server still prices every
 * settlement and answers 409 `total_mismatch` on any disagreement; these
 * functions exist so the button can name the right number BEFORE the counter
 * presses it, and so the `expectedTotalCents` it sends is the number the guest
 * was shown.
 *
 * THE SPLIT RULE, restated because it has to match to the centavo: every share
 * is `floor(total / parts)`, and the first `total % parts` shares take one
 * extra centavo. Shares sum to exactly the total and land on the LOW part
 * numbers, deterministically, which is what makes re-quoting part 2 safe.
 */

/** The shared line (la botella) is settled by naming the null slot. */
export const SHARED_SLOT = null;

export function isUnpaid(item: TabItem): boolean {
  return item.settledChargeId === null;
}

export function sumLines(items: readonly TabItem[]): number {
  return items.reduce((total, item) => total + item.lineTotalCents, 0);
}

/** Every puesto with an unpaid line on it, shared first, then ascending. */
export function unpaidGuestSlots(tab: TabRecord): (number | null)[] {
  const slots = new Set<number | null>();
  for (const item of tab.items) if (isUnpaid(item)) slots.add(item.guestSlot);
  const numbered = [...slots].filter((slot): slot is number => slot !== null).sort((a, b) => a - b);
  return slots.has(SHARED_SLOT) ? [SHARED_SLOT, ...numbered] : numbered;
}

/** What one puesto still owes for its own lines. */
export function slotTotalCents(tab: TabRecord, guestSlot: number | null): number {
  return sumLines(tab.items.filter((item) => isUnpaid(item) && item.guestSlot === guestSlot));
}

export function equalSplitShares(totalCents: number, parts: number): number[] {
  if (!Number.isInteger(parts) || parts < 1) return [];
  return sharedEqualSplitShares(totalCents, parts);
}

/** One share by its 1-based part number, which is how the counter names it. */
export function equalSplitShareCents(totalCents: number, parts: number, part: number): number {
  return equalSplitShares(totalCents, parts)[part - 1] ?? 0;
}

/**
 * The parts of an equal split nobody has paid yet. A tab already splitting is
 * locked to its own `parts`, so that count wins over whatever the control
 * currently shows.
 */
export function unpaidSplitParts(tab: TabRecord, requestedParts: number): number[] {
  const started = tab.splitPayments[0];
  const parts = started ? started.parts : requestedParts;
  const paid = new Set(tab.splitPayments.map((payment) => payment.part));
  return Array.from({ length: parts }, (_unused, index) => index + 1).filter(
    (part) => !paid.has(part),
  );
}

/**
 * A tab settles in ONE mode, decided by its first settlement: by guest, or in
 * equal shares. They cannot mix, because a share is a claim on every line
 * including one another guest already paid.
 */
export function settledMode(tab: TabRecord): 'guest' | 'split' | undefined {
  if (tab.splitPayments.length > 0) return 'split';
  return tab.items.some((item) => !isUnpaid(item)) ? 'guest' : undefined;
}
